import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ItemType } from '@hero/schema';
import { useBayanGoodTypes } from '@hero/hooks-shared';
import { SelectAuto, type SelectAutoOption } from '../../../app/Select';

export interface BayanGoodTypeSelectProps {
  value: number | undefined;
  onChange: (goodTypeId: number | undefined) => void;
  /**
   * Narrows the list to the branch this Hero item type belongs to.
   *
   * A construction load should be choosing between Concrete, Steel and Cement,
   * not scrolling 159 entries that include Uranium and Live Camels. Omit to
   * offer everything, which is what the Org-wide default wants.
   */
  narrowToItemType?: ItemType;
  /** The branch a waybill blocker named; wins over `narrowToItemType`. */
  narrowToBranchId?: number;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Pick a cargo type from Bayan's own taxonomy.
 *
 * ## Why the labels aren't in `@hero/lang`
 *
 * ELM ships both an Arabic and an English name for every node, and the list
 * runs to ~160 entries that change on ELM's schedule. Mirroring that into our
 * locale files would create a second source of truth for a government
 * catalogue we don't own, so the API's own bilingual names are rendered
 * directly and the active language just picks which column to read.
 */
export function BayanGoodTypeSelect({
  value,
  onChange,
  narrowToItemType,
  narrowToBranchId,
  placeholder,
  disabled = false,
  className,
}: BayanGoodTypeSelectProps) {
  const { t } = useTranslation(['m-apps']);
  const { options: goodTypes, isPending } = useBayanGoodTypes({
    narrowToItemType,
    narrowToBranchId,
  });

  const options: SelectAutoOption[] = useMemo(
    () => goodTypes.map(option => ({ value: String(option.id), label: option.label })),
    [goodTypes],
  );

  return (
    <SelectAuto
      options={options}
      value={value === undefined ? undefined : String(value)}
      onChange={selected => onChange(selected ? Number(selected) : undefined)}
      placeholder={
        isPending
          ? t('m-apps:form.default_good_type.loading')
          : placeholder ?? t('m-apps:form.default_good_type.placeholder')
      }
      disabled={disabled || isPending}
      className={className}
    />
  );
}
